import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { releasesService } from '@/services/api/releases.service';
import type {
  AdvanceDeploymentRequest,
  DeploymentListQuery,
  PublishVersionRequest,
  StartDeploymentRequest,
  UpdateVersionRequest,
  VersionListQuery,
} from '@/types';
import { queryKeys } from './keys';

export function useVersions(query: VersionListQuery) {
  return useQuery({
    queryKey: queryKeys.releases.versions(query),
    queryFn: ({ signal }) => releasesService.versions(query, signal),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

export function useDeployments(query: DeploymentListQuery) {
  return useQuery({
    queryKey: queryKeys.releases.deployments(query),
    queryFn: ({ signal }) => releasesService.deployments(query, signal),
    placeholderData: keepPreviousData,
    /*
     * Shorter than versions and polled, because this is the list somebody
     * watches while a rollout is in flight. A stage that changed a minute ago
     * and is still showing 'building' is the one thing this table must not do.
     */
    staleTime: 10 * 1000,
    refetchInterval: 15 * 1000,
  });
}

export function usePublishVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: PublishVersionRequest) => releasesService.publish(body),
    onSuccess: async (version) => {
      toast.success(`Published ${version.versionNumber}.`);
      if (!version.codeSigned) {
        /*
         * Worth interrupting for. An unsigned installer is refused by Windows
         * on the customer's machine, and finding that out from a support ticket
         * costs considerably more than reading it here.
         */
        toast.warning('This build is not code signed. Windows will refuse to install it.');
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.releases.all });
      // The dashboard shows the latest stable version.
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useUpdateVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ versionId, body }: { versionId: string; body: UpdateVersionRequest }) =>
      releasesService.update(versionId, body),
    onSuccess: async (version) => {
      toast.success(`Saved ${version.versionNumber}.`);
      await queryClient.invalidateQueries({ queryKey: queryKeys.releases.all });
      // The dashboard shows whether the latest stable version is mandatory.
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useVersionAdoption(days: number) {
  return useQuery({
    queryKey: queryKeys.releases.adoption(days),
    queryFn: ({ signal }) => releasesService.adoption(days, signal),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}

export function useStartDeployment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: StartDeploymentRequest) => releasesService.start(body),
    onSuccess: async () => {
      toast.success('Deployment started.');
      await queryClient.invalidateQueries({ queryKey: queryKeys.releases.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useApproveDeployment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deploymentId: string) => releasesService.approve(deploymentId),
    onSuccess: async () => {
      toast.success('Rollout approved. It can go live now.');
      await queryClient.invalidateQueries({ queryKey: queryKeys.releases.all });
    },
  });
}

export function useAdvanceDeployment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      deploymentId,
      body,
    }: {
      deploymentId: string;
      body: AdvanceDeploymentRequest;
    }) => releasesService.advance(deploymentId, body),
    onSuccess: async (deployment) => {
      toast.success(`Moved to ${deployment.stage.replace(/_/g, ' ')}.`);
      await queryClient.invalidateQueries({ queryKey: queryKeys.releases.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}
